import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getOrders, updateOrderStatus } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { 
  ShoppingCart, 
  Package, 
  Building2, 
  Clock, 
  CheckCircle2, 
  Truck, 
  XCircle,
  MapPin,
  Calendar,
  ChevronRight,
  Loader2,
  ArrowUpRight,
  ArrowDownLeft
} from 'lucide-react';
import { toast } from 'sonner';

const getOrderStatuses = (isUSA) => ({
  pendiente: { label: isUSA ? 'Pending' : 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  confirmado: { label: isUSA ? 'Confirmed' : 'Confirmado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle2 },
  en_produccion: { label: isUSA ? 'In production' : 'En producción', color: 'bg-purple-100 text-purple-800', icon: Package },
  enviado: { label: isUSA ? 'Shipped' : 'Enviado', color: 'bg-indigo-100 text-indigo-800', icon: Truck },
  entregado: { label: isUSA ? 'Delivered' : 'Entregado', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  cancelado: { label: isUSA ? 'Cancelled' : 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle }
});

const OrderList = () => {
  const { user } = useAuth();
  const { isUSA } = useLocale();
  const ORDER_STATUSES = getOrderStatuses(isUSA);
  const [receivedOrders, setReceivedOrders] = useState([]);
  const [sentOrders, setSentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('received');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [companyType, setCompanyType] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get company type
      try {
        const company = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies/my`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}` }
        }).then(r => r.json());
        setCompanyType(company.company_type || 'ambos');
      } catch (e) {
        setCompanyType('ambos'); // Default to both if no company
      }

      const [received, sent] = await Promise.all([
        getOrders('received'),
        getOrders('sent')
      ]);
      setReceivedOrders(received);
      setSentOrders(sent);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const isProvider = companyType === 'proveedor' || companyType === 'ambos';
  const isManufacturer = companyType === 'fabricante' || companyType === 'ambos';

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdating(true);
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success(isUSA ? 'Status updated' : 'Estado actualizado');
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error(error.message || (isUSA ? 'Error updating' : 'Error al actualizar'));
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(isUSA ? 'en-US' : 'es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (!price) return isUSA ? 'Quote' : 'A cotizar';
    return `$${price.toLocaleString(isUSA ? 'en-US' : 'es-MX')} ${isUSA ? 'USD' : 'MXN'}`;
  };

  const OrderCard = ({ order, type }) => {
    const status = ORDER_STATUSES[order.status] || ORDER_STATUSES.pendiente;
    const StatusIcon = status.icon;
    const isReceived = type === 'received';

    return (
      <Card className="card-industrial hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Badge className={status.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
              <span className="text-xs text-slate-400">#{order.order_id.slice(-6)}</span>
            </div>
            <span className="text-xs text-slate-500">{formatDate(order.created_at)}</span>
          </div>

          <div className="flex items-start gap-3 mb-3">
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Package className="w-6 h-6 text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 truncate">{order.product_name}</h3>
              <p className="text-sm text-slate-500">
                {isUSA ? 'Quantity: ' : 'Cantidad: '}<span className="font-medium">{order.quantity}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
            <Building2 className="w-4 h-4" />
            <span>
              {isReceived ? (
                <>{isUSA ? 'Buyer: ' : 'Comprador: '}<strong>{order.buyer_company_name}</strong></>
              ) : (
                <>{isUSA ? 'Supplier: ' : 'Proveedor: '}<strong>{order.seller_company_name}</strong></>
              )}
            </span>
          </div>

          {order.shipping_address && (
            <div className="flex items-start gap-2 text-sm text-slate-500 mb-3">
              <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="line-clamp-1">{order.shipping_address}</span>
            </div>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <span className="font-bold text-lg" style={{ color: '#1E4C8E' }}>
              {formatPrice(order.total_price)}
            </span>
            
            {isReceived && order.status !== 'entregado' && order.status !== 'cancelado' ? (
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" onClick={() => setSelectedOrder(order)}>
                    {isUSA ? 'Update status' : 'Actualizar estado'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{isUSA ? 'Update order status' : 'Actualizar estado del pedido'}</DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <p className="text-sm text-slate-600 mb-4">
                      {isUSA ? 'Order: ' : 'Pedido: '}<strong>{order.product_name}</strong> x {order.quantity}
                    </p>
                    <Select
                      defaultValue={order.status}
                      onValueChange={(value) => handleStatusChange(order.order_id, value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pendiente">{isUSA ? 'Pending' : 'Pendiente'}</SelectItem>
                        <SelectItem value="confirmado">{isUSA ? 'Confirmed' : 'Confirmado'}</SelectItem>
                        <SelectItem value="en_produccion">{isUSA ? 'In production' : 'En producción'}</SelectItem>
                        <SelectItem value="enviado">{isUSA ? 'Shipped' : 'Enviado'}</SelectItem>
                        <SelectItem value="entregado">{isUSA ? 'Delivered' : 'Entregado'}</SelectItem>
                        <SelectItem value="cancelado">{isUSA ? 'Cancelled' : 'Cancelado'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <Link to={`/company/${isReceived ? order.buyer_company_id : order.seller_company_id}`}>
                <Button size="sm" variant="ghost">
                  {isUSA ? 'View company' : 'Ver empresa'}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="orders-page">
      <div className="page-header">
        <h1 className="page-title flex items-center gap-2">
          <ShoppingCart className="w-6 h-6" style={{ color: '#1E4C8E' }} />
          {isUSA ? 'Orders' : 'Pedidos'}
        </h1>
        <p className="page-subtitle">
          {isUSA ? 'Manage your orders placed and received' : 'Gestiona tus pedidos realizados y recibidos'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          {isProvider && (
            <TabsTrigger value="received" className="flex items-center gap-2">
              <ArrowDownLeft className="w-4 h-4" />
              {isUSA ? 'Received' : 'Recibidos'} ({receivedOrders.length})
            </TabsTrigger>
          )}
          {isManufacturer && (
            <TabsTrigger value="sent" className="flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4" />
              {isUSA ? 'Placed' : 'Realizados'} ({sentOrders.length})
            </TabsTrigger>
          )}
        </TabsList>

        {isProvider && (
          <TabsContent value="received">
            {receivedOrders.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="font-semibold text-slate-700 mb-2">{isUSA ? "You don't have received orders" : 'No tienes pedidos recibidos'}</h3>
                  <p className="text-slate-500 text-sm">
                    {isUSA ? 'Orders from your customers will appear here' : 'Los pedidos de tus clientes aparecerán aquí'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {receivedOrders.map((order) => (
                  <OrderCard key={order.order_id} order={order} type="received" />
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {isManufacturer && (
          <TabsContent value="sent">
            {sentOrders.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="font-semibold text-slate-700 mb-2">{isUSA ? "You haven't placed orders" : 'No has realizado pedidos'}</h3>
                  <p className="text-slate-500 text-sm mb-4">
                    {isUSA ? 'Search for suppliers and place your first order' : 'Busca proveedores y realiza tu primer pedido'}
                  </p>
                  <Link to="/dashboard/providers">
                    <Button style={{ backgroundColor: '#34B772' }}>
                      {isUSA ? 'Search suppliers' : 'Buscar proveedores'}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sentOrders.map((order) => (
                  <OrderCard key={order.order_id} order={order} type="sent" />
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default OrderList;
